import { useState, FormEvent } from 'react';
import { FileText, Plus, Sparkles, PenLine, GraduationCap, Paperclip, ArrowUp, LucideIcon } from 'lucide-react';
import { usePrototype3 } from '../Prototype3Context';
import { SUGGESTIONS, Suggestion } from '../prototype3Data';

const SUGGESTION_ICONS: Record<string, LucideIcon> = {
  'start-introduction': PenLine,
  'review-postsecondary': GraduationCap,
};

const Screen01Welcome = () => {
  const { isChatPending, sendUserMessage, setCurrentSectionId, goToScreen } = usePrototype3();
  const [message, setMessage] = useState('');

  const submitMessage = async (text: string, suggestion?: Suggestion) => {
    if (!text.trim() || isChatPending) return;
    const targetSectionId = suggestion?.sectionId ?? 'postsecondary-readiness';
    setCurrentSectionId(targetSectionId);
    setMessage('');
    goToScreen('02-draft-builder');
    await sendUserMessage(text, {
      sliceOverride: {
        active_section_id: targetSectionId,
        ui_state: { panel: 'chat-open', last_user_action: 'first-message' },
      },
    });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void submitMessage(message);
  };

  const handleSuggestion = (suggestion: Suggestion) => {
    void submitMessage(suggestion.prompt, suggestion);
  };

  return (
    <div className="flex flex-col h-full w-full bg-[#fafafa] font-['Inter']">
      <header className="flex items-center gap-3 px-6 h-14 border-b border-[#e5e5e5] bg-[#fafafa] shrink-0">
        <FileText className="w-5 h-5 text-[#0a0a0a]" />
        <span className="text-[15px] font-semibold text-[#0a0a0a]">EOAA Report Builder</span>
        <span className="text-sm text-[#737373]">|</span>
        <span className="text-sm text-[#737373]">Issaquah School District - 2023-24</span>
        <div className="flex-1" />
        <button
          type="button"
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[#e5e5e5] rounded-md bg-white hover:bg-gray-50 text-[#0a0a0a] transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Report
        </button>
      </header>

      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center gap-2.5 px-6 h-[52px] border-b border-[#e5e5e5] shrink-0">
          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#171717] shrink-0">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-[#0a0a0a]">AI Draft Assistant</span>
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-[#f5f5f5] text-[#737373] border border-[#e5e5e5]">
            Ready
          </span>
        </div>

        <div className="flex flex-col flex-1 items-center justify-center px-20 min-h-0 overflow-y-auto">
          <div className="flex flex-col items-center gap-6 w-[600px]">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#171717]">
              <Sparkles className="w-7 h-7 text-white" />
            </div>

            <h1 className="text-2xl font-bold text-[#0a0a0a] text-center">Let's build your EOAA report</h1>

            <p className="text-[15px] text-[#737373] text-center leading-relaxed w-full">
              I can help you draft, review, and refine each section of the Educational Outcomes Audit &amp; Analysis for
              Issaquah School District. Tell me where you'd like to start — or I can walk you through it section by
              section.
            </p>

            <div className="flex items-center gap-2.5 flex-wrap justify-center w-full">
              {SUGGESTIONS.map((s) => {
                const Icon = SUGGESTION_ICONS[s.id] ?? PenLine;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => handleSuggestion(s)}
                    disabled={isChatPending}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#fafafa] border border-[#e5e5e5] text-sm font-medium text-[#0a0a0a] hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    <Icon className="w-3.5 h-3.5 text-[#737373]" />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex items-center gap-2.5 px-6 h-16 border-t border-[#e5e5e5] shrink-0"
        >
          <button type="button" className="shrink-0 hover:opacity-70 transition-opacity">
            <Paperclip className="w-[18px] h-[18px] text-[#737373]" />
          </button>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Tell me about the report you'd like to create..."
            disabled={isChatPending}
            className="flex-1 h-10 px-3 text-[13px] text-[#0a0a0a] placeholder:text-[#737373] rounded-md bg-[#fafafa] border border-[#e5e5e5] outline-none focus:border-[#a3a3a3] transition-colors"
          />
          <button
            type="submit"
            disabled={!message.trim() || isChatPending}
            className="flex items-center justify-center w-9 h-9 rounded-md bg-[#171717] shrink-0 hover:bg-[#2a2a2a] disabled:opacity-40 transition-colors"
          >
            <ArrowUp className="w-[18px] h-[18px] text-white" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default Screen01Welcome;
